/* ============================================================================
   RYVT PDP controller — spec §1.4 / §4 AGENT 4. Vanilla, ≤10KB min.
   - <ryvt-variant-picker>: option radios/selects -> variant resolve -> URL
     ?variant= replaceState -> emits ryvt:variant:change {variant, product,
     color}. All swaps are instant text swaps; ZERO client-side money math —
     every price string is server-rendered in the Liquid price bank and only
     ever copied verbatim.
   - <ryvt-gallery>: per-color filter ported from theme/assets/
     ryvt-color-gallery.js (color/kind attribution happens entirely in Liquid;
     this only toggles `hidden` + reorders — never re-fetches). Re-sort is
     synchronous with a 160ms opacity mask (--ryvt-t-mask) over every visible
     item, since >=990px the gallery is a stacked column, not a carousel.
   - <ryvt-sticky-bar>: ported IO logic from theme/assets/ryvt-sticky-atc.js
     including the `boundingClientRect.bottom < 0` direction test; R10
     trigger: visible only when the ATC block is fully above the viewport AND
     the user is scrolling down. Mobile: visible unless the inline primary
     button is wholly on screen (see _measure).
   - Sheet wiring (size-guide tabs + lazy recommender import + notify) and
     tap-to-zoom overlay.
   Events consumed/emitted are the §3.4 closed set only.
   ========================================================================== */
(() => {
  /* Load-order safety net. The runtime now loads in <head> so window.RYVT is
     always defined by the time this runs — but a bare `if (!R) return` is a
     SILENT failure that once killed this entire file (variant picker, gallery,
     sticky bar, size guide and notify) with a clean console.
     Module scripts are deferred, so every one of them executes before
     DOMContentLoaded: retrying there recovers regardless of tag order. */
  function init() {
  const R = window.RYVT;
  if (!R) {
    console.error('[RYVT] runtime missing — PDP interaction layer inactive.');
    return;
  }

  const D = document;
  const q = (sel, root) => (root || D).querySelector(sel);
  const qa = (sel, root) => Array.from((root || D).querySelectorAll(sel));
  const A = (n) => '[data-ryvt-' + n + ']';
  const tog = (el, cls, v) => el.classList.toggle(cls, v);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
  const behavior = () => (reduced.matches ? 'auto' : 'smooth');
  const SECTION = '[data-ryvt-section="main-product"]';
  const OPT_INPUT = A('option-input');
  const SIZE_FIELDSET = '[data-option-name="Size"]';
  const SIZE_INPUT = SIZE_FIELDSET + ' input' + OPT_INPUT;
  const COLOR_INPUT = '[data-option-name="Color"] input' + OPT_INPUT;

  /* -- <ryvt-variant-picker> ------------------------------------------------- */
  if (!customElements.get('ryvt-variant-picker')) {
    customElements.define(
      'ryvt-variant-picker',
      class extends HTMLElement {
        connectedCallback() {
          const dataEl = q(A('variant-data'), this);
          try {
            this.data = JSON.parse(dataEl.textContent);
          } catch (e) {
            this.data = null;
          }
          if (!this.data) return;
          this.section = this.closest(SECTION);
          this.threshold = Number(this.dataset.lowStockThreshold) || 7;
          this.countThreshold = Number(this.dataset.lowStockCountThreshold) || 5;
          this.form = q(A('product-form'), this.section);
          this.alertEl = q(A('form-alert'), this.section);

          const viewedVariant = this.resolve(this.readValues());
          R.track('product_view', {
            product_id: Number(this.data.product.id),
            product_handle: this.data.product.handle,
            variant_id: viewedVariant ? Number(viewedVariant.id) : undefined,
            available: viewedVariant ? !!viewedVariant.available : !!this.data.variants.some((v) => v.available),
          });

          /* Liquid ships Add to cart `disabled` so a PDP whose JS never arrived
             cannot post an empty variant id. That guard has now been taken over by
             handleSubmit, so hand the button back to the keyboard: aria-disabled
             keeps announcing "not yet", and activating it scrolls to the size row
             and pulses it instead of failing silently. */
          const atc = q(A('atc'), this.section);
          if (atc && atc.disabled) {
            atc.disabled = false;
            atc.setAttribute('aria-disabled', 'true');
          }

          this._ac = new AbortController();
          const s = { signal: this._ac.signal };
          this.addEventListener(
            'change',
            (e) => {
              const input = e.target.closest(OPT_INPUT);
              if (!input) return;
              const isSize = !!input.closest(SIZE_FIELDSET);
              const via = this._sizeVia;
              this._sizeVia = null;
              /* Only a tap, Space or Enter on a size tile answers the arm.
                 Arrow keys move through the group and keep it unless they land
                 on a sold-out size. Any other writer (a colour change, the size
                 recommender) disarms without adding: the add was asked for
                 under a different choice. */
              const resume = this._armed;
              const keep = isSize && via === 'arrow' && (this.resolve(this.readValues()) || {}).available;
              if (!keep) this.disarm();
              this.update();
              if (resume && isSize && via === 'tap') this.resumeArmed(resume);
            },
            s
          );
          /* Enter/Space on a focused size behave like a tap. Enter would
             otherwise submit the form from inside the radio group. */
          this.addEventListener(
            'keydown',
            (e) => {
              const input = e.target.closest(SIZE_FIELDSET + ' input' + OPT_INPUT);
              this._sizeVia = input && String(e.key).indexOf('Arrow') === 0 ? 'arrow' : null;
              if (!input || (e.key !== 'Enter' && e.key !== ' ')) return;
              e.preventDefault();
              if (!input.checked) {
                input.checked = true;
                this._sizeVia = 'tap';
                input.dispatchEvent(new Event('change', { bubbles: true }));
              } else this.resumeSelected();
            },
            s
          );
          /* A click on a size (direct or via its label) is a tap. Chrome and
             Firefox also fire click when arrow keys move the selection, so an
             arrow keeps its provenance. If no change follows, the shopper
             tapped the size that was already selected (e.g. reached by arrow
             keys): that is still the answer to the arm. */
          this.addEventListener(
            'click',
            (e) => {
              if (!e.target.closest(SIZE_FIELDSET + ' input' + OPT_INPUT)) return;
              if (this._sizeVia !== 'arrow') this._sizeVia = 'tap';
              setTimeout(() => {
                if (this._sizeVia !== 'tap') return;
                this._sizeVia = null;
                this.resumeSelected();
              });
            },
            s
          );
          if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e), s);

          /* Redesign §7.1 — the offer module's "Add 3" shortcut. Same
             fail-closed path as the ATC: no size -> scroll-and-pulse recovery,
             sold out -> notify sheet, and nothing posts without a resolved
             variant id. */
          if (this.section) {
            this.section.addEventListener(
              'click',
              (e) => {
                const bundleBtn = e.target.closest(A('add-bundle'));
                if (bundleBtn) this.addBundle(bundleBtn);
              },
              s
            );
          }

          /* The picker upgrades before ryvt-gallery is registered below. Wait
             until all elements have connected so the gallery receives the URL
             color change instead of keeping its server-rendered default. */
          queueMicrotask(() => {
            if (this.isConnected) this.applyUrlColor();
          });
        }

        /* A colour carried over from a product card (ryvt-runtime.js writes
           ?color= onto both card links when a swatch is clicked).

           Deliberately NOT ?variant=: main-product.liquid derives size_chosen
           from product.selected_variant, so a variant id in the URL would mark a
           size as chosen and hand the shopper an armed Add to cart for a size
           they never picked. Colour is the only thing the swatch said, so colour
           is the only thing carried.

           Routed through the same input + change event the chips use, so the
           gallery, the legend, size availability and the model-fit line all
           follow exactly as if it had been clicked — no second code path.

           Runs through the normal change path so color never implies a size. */
        applyUrlColor() {
          try {
            const wanted = new URLSearchParams(window.location.search).get('color');
            if (!wanted) return;
            const target = qa(COLOR_INPUT, this).find((input) => norm(input.value) === norm(wanted));
            /* An unknown colour is a stale or hand-edited link, not an error
               worth surfacing: leave the server's default selected. */
            if (target && !target.checked) {
              target.checked = true;
              target.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } catch (e) {
            /* Defensive: URLSearchParams' decoder is lenient (it substitutes
               U+FFFD rather than throwing), so this is not known to be
               reachable. Kept so a future, stricter parsing step here still
               fails safe — keep the default colour rather than surface an
               error. */
          }
        }

        disconnectedCallback() {
          if (this._ac) this._ac.abort();
        }

        readValues() {
          const values = [];
          qa(A('option'), this).forEach((fs) => {
            const input = q('input' + OPT_INPUT + ':checked, select' + OPT_INPUT, fs);
            values[Number(fs.dataset.optionIndex)] = input && input.value ? input.value : null;
          });
          return values;
        }

        resolve(values) {
          const list = this.data.variants;
          if (!list.length) return null;
          if (values.some((v, i) => v == null && i < list[0].options.length)) return null;
          return list.find((v) => v.options.every((opt, i) => opt === values[i])) || null;
        }

        isLow(v) {
          return !!(
            v &&
            v.available &&
            v.managed &&
            v.inventoryPolicy === 'deny' &&
            v.qty > 0 &&
            v.qty <= this.threshold
          );
        }

        update() {
          const data = this.data;
          const sec = this.section;
          const values = this.readValues();
          const color = data.colorIndex > -1 ? values[data.colorIndex] || '' : '';
          const variant = this.resolve(values);

          /* legends echo the selection ("Color — Army Green") */
          qa(A('option'), this).forEach((fs) => {
            const span = q(A('legend-value'), fs);
            const v = values[Number(fs.dataset.optionIndex)];
            if (span) span.textContent = v ? ' — ' + v : '';
          });

          /* size availability for the active color (struck-through, still tappable)
             plus its stock micro-label: same thresholds as the stock line below,
             short cell-width copy, never a count above five */
          const low = q(A('low-stock'), sec);
          if (data.sizeIndex > -1) {
            qa(A('size-cell'), this).forEach((cell) => {
              const input = q(OPT_INPUT, cell);
              if (!input) return;
              const hit = data.variants.find(
                (v) =>
                  v.available &&
                  v.options[data.sizeIndex] === input.value &&
                  (data.colorIndex < 0 || !color || v.options[data.colorIndex] === color)
              );
              tog(cell, 'is-unavailable', !hit);
              const state = q(A('size-state'), cell);
              if (!state) return;
              let text = '';
              const strs = cell.parentElement.dataset;
              if (!hit) text = strs.strSoldOut || '';
              else if (this.isLow(hit)) {
                const tpl = hit.qty <= this.countThreshold ? strs.strLowCount : strs.strLow;
                text = tpl ? tpl.replace('[n]', String(hit.qty)) : '';
              }
              state.textContent = text;
              state.hidden = !text;
            });
          }

          /* price: verbatim copy from the server-rendered bank (no formatting) */
          const priceTarget = q(A('price-target'), sec);
          const bankEntry = variant ? q(A('variant-price="' + variant.id + '"'), this) : null;
          const unresolvedEntry = q(A('unresolved-price'), this);
          const priceSource = bankEntry || unresolvedEntry;
          if (priceSource && priceTarget) {
            const wrap = q('.ryvt-price', priceTarget) || priceTarget;
            const sourceWrap = q('.ryvt-price', priceSource) || priceSource;
            wrap.replaceChildren.apply(
              wrap,
              Array.from(sourceWrap.childNodes, (node) => node.cloneNode(true))
            );
          }

          /* ATC / notify state + hidden input */
          const atc = q(A('atc'), sec);
          const notify = q(A('atc-notify'), sec);
          const idInput = q(A('variant-input'), sec);
          const soldOut = !!(variant && !variant.available);
          if (atc) {
            atc.hidden = soldOut;
            /* aria-disabled, never the `disabled` property: the button must stay
               in the tab order so a keyboard shopper meets it, and activating it
               without a size runs the scroll-and-pulse recovery instead of dying
               silently. Fail-closed lives in handleSubmit, which will not post
               without a resolved variant id — not in the button's enabled state. */
            const prompt = q(A('atc-prompt'), atc);
            const mainLabel = q(A('atc-main'), atc);
            if (variant) {
              atc.removeAttribute('aria-disabled');
              if (prompt) prompt.hidden = true;
              if (mainLabel) mainLabel.hidden = false;
            } else {
              atc.setAttribute('aria-disabled', 'true');
              if (prompt) prompt.hidden = false;
              if (mainLabel) mainLabel.hidden = true;
            }
            const priceSpan = q(A('atc-price'), atc);
            if (priceSpan && bankEntry) priceSpan.textContent = bankEntry.dataset.priceText || priceSpan.textContent;
          }
          if (notify) notify.hidden = !soldOut;
          /* Shopify's wallet button cannot be disabled, so it is wrapped and hidden
             until a variant resolves — otherwise a live "Buy it now" sits under a
             greyed-out ATC and checks out whatever variant the form last held. */
          const dynamicCheckout = q(A('dynamic-checkout'), sec);
          if (dynamicCheckout) dynamicCheckout.hidden = !variant || soldOut;
          const dynamicCheckoutPrompt = q(A('dynamic-checkout-prompt'), sec);
          if (dynamicCheckoutPrompt) dynamicCheckoutPrompt.hidden = !!variant;
          if (idInput) idInput.value = variant ? String(variant.id) : '';
          if (this.alertEl) this.alertEl.hidden = true;
          const sizePrompt = q(A('size-prompt'), this);
          if (sizePrompt && !this._armed) sizePrompt.hidden = true;

          /* Inventory urgency stays quiet until the resolved variant drops below
             eight units. Six or seven gets the state only; five or fewer gets
             the exact count. Templates remain server-rendered locale strings. */
          if (low) {
            const lowNow = this.isLow(variant);
            low.hidden = !lowNow;
            if (lowNow) {
              const tpl = variant.qty <= this.countThreshold ? low.dataset.strLowCount : low.dataset.strLow;
              if (tpl) low.textContent = tpl.replace('[n]', String(variant.qty));
            }
          }

          /* model-fit honesty line: server-rendered spans, JS toggles hidden.
             The WRAPPER is toggled too. Colours the metafield does not cover
             leave every span hidden, and a <p> with no visible child still
             occupies its own box — which is what put a blank line under the
             size row on every uncovered colourway. */
          const colorKey = norm(color);
          let fitVisible = 0;
          qa(A('fit-color'), sec).forEach((span) => {
            span.hidden = !colorKey || norm(span.dataset.ryvtFitColor) !== colorKey;
            if (!span.hidden) fitVisible += 1;
          });
          const fitWrap = q(A('model-fit'), sec);
          if (fitWrap) fitWrap.hidden = fitVisible === 0;

          /* notify sheet mirrors the selection */
          const nLabel = q(A('notify-variant-label'));
          const nInput = q(A('notify-variant'));
          const title = data.showVariantTitle && variant ? variant.title : '';
          if (nLabel) nLabel.textContent = title ? ' · ' + title : '';
          if (nInput) nInput.value = title;

          if (variant) {
            try {
              const nextUrl = new URL(window.location.href);
              nextUrl.searchParams.set('variant', variant.id);
              nextUrl.searchParams.delete('color');
              history.replaceState(null, '', nextUrl);
            } catch (e) {
              /* sandboxed contexts */
            }
          }

          R.emit('ryvt:variant:change', { variant, product: data.product, color });
          if (variant) {
            R.track('variant_select', {
              product_id: Number(data.product.id),
              product_handle: data.product.handle,
              variant_id: Number(variant.id),
              available: !!variant.available,
            });
          }
        }

        /* No-size recovery for the inline CTA, the sticky bar and Add 3: bring
           the size row under the pinned header (scroll-margin-top in the
           section CSS), focus the first in-stock size and say why beside it.
           `resume` re-runs the triggering control's own path; the picker holds
           it "armed" so the shopper's next size tap finishes the add in one
           step. A colourway with nothing in stock never arms: it focuses its
           first size and asks for one, so the notify sheet always opens on a
           resolved variant. */
        pulseSizes(resume) {
          const anchor = q(A('size-anchor'), this);
          if (!anchor) return;
          const inputs = qa('input' + OPT_INPUT, anchor);
          const firstOpen = inputs.find((input) => !input.closest('.is-unavailable'));
          const prompt = q(A('size-prompt'), anchor);
          if (prompt) {
            prompt.textContent = (firstOpen ? prompt.dataset.strDefault : prompt.dataset.strSoldOut) || '';
            prompt.hidden = false;
          }
          if (this.alertEl) this.alertEl.hidden = true;
          anchor.scrollIntoView({ behavior: behavior(), block: 'start' });
          const firstSize = firstOpen || inputs[0];
          if (firstSize) firstSize.focus({ preventScroll: true });
          const cls = firstOpen && resume ? 'is-armed' : 'is-pulsing';
          this._armed = cls === 'is-armed' ? resume : null;
          anchor.classList.remove('is-armed', 'is-pulsing');
          anchor.offsetHeight;
          anchor.classList.add(cls);
          if (cls === 'is-pulsing') setTimeout(() => anchor.classList.remove('is-pulsing'), 520);
        }

        disarm() {
          this._armed = null;
          const anchor = q(A('size-anchor'), this);
          if (anchor) anchor.classList.remove('is-armed');
        }

        /* Only an in-stock resolved size completes the add; a sold-out pick
           stays selected and the notify CTA takes over as before. The resumed
           path re-runs every fail-closed guard regardless. */
        resumeArmed(resume) {
          const variant = this.resolve(this.readValues());
          if (variant && variant.available) resume();
        }

        resumeSelected() {
          if (!this._armed) return;
          const resume = this._armed;
          this.disarm();
          this.update();
          this.resumeArmed(resume);
        }

        addBundle(btn) {
          if (this._pending) return;
          const values = this.readValues();
          if (this.data.sizeIndex > -1 && !values[this.data.sizeIndex]) {
            this.pulseSizes(() => this.addBundle(btn));
            return;
          }
          const variant = this.resolve(values);
          if (!variant) {
            this.pulseSizes(() => this.addBundle(btn));
            return;
          }
          if (!variant.available) {
            openSheet('notify', btn);
            return;
          }
          const quantity = Math.max(1, Number(btn.dataset.ryvtAddBundle) || 3);
          this._pending = true;
          btn.setAttribute('aria-busy', 'true');
          const settle = () => {
            this._pending = false;
            btn.removeAttribute('aria-busy');
          };
          R.cart('add', { items: [{ id: variant.id, quantity }] }, 'product_offer')
            .then(settle)
            .catch((err) => {
              settle();
              if (this.alertEl) {
                this.alertEl.textContent = (err && err.message) || 'Something went wrong.';
                this.alertEl.hidden = false;
              }
            });
        }

        handleSubmit(e) {
          e.preventDefault();
          /* The button no longer goes `disabled` in flight (that would drop focus
             out of a control the shopper just activated), so re-entry is guarded
             here instead — a double Enter must not post the item twice. */
          if (this._pending) return;
          const values = this.readValues();
          /* no size selected: scroll + opacity pulse — never a silent failure */
          if (this.data.sizeIndex > -1 && !values[this.data.sizeIndex]) {
            this.pulseSizes(() => this.handleSubmit(e));
            return;
          }
          const variant = this.resolve(values);
          if (variant && !variant.available) {
            openSheet('notify', q(A('atc-notify'), this.section));
            return;
          }
          const idInput = q(A('variant-input'), this.section);
          const id = variant ? variant.id : Number(idInput && idInput.value);
          if (!id) return;
          const atc = q(A('atc'), this.section);
          this._pending = true;
          if (atc) atc.setAttribute('aria-busy', 'true');
          const settle = () => {
            this._pending = false;
            if (atc) atc.removeAttribute('aria-busy');
          };
          R.cart('add', { items: [{ id, quantity: 1 }] }, 'product_page')
            .then(settle)
            .catch((err) => {
              settle();
              if (this.alertEl) {
                this.alertEl.textContent = (err && err.message) || 'Something went wrong.';
                this.alertEl.hidden = false;
              }
            });
        }
      }
    );
  }

  /* -- <ryvt-gallery> (port of ryvt-color-gallery.js filter logic) ----------- */
  /* Desktop is an editorial grid with no dots, no thumbs and no horizontal
     scroll (see main-product.liquid's >=990px block). Driving scrollLeft or
     syncing an active dot there would fight a layout that has neither.
     989.98px, not 989px: the CSS switches at (min-width: 990px), so a plain
     989 leaves the band 989 < w < 990 — reachable by browser zoom and
     fractional DPR — matching neither query. There the CSS would paint the
     carousel and its dots while this took the stacked-column branch, so a
     colour change would reset no scroll and sync no dot. */
  const carousel = matchMedia('(max-width: 989.98px)');

  if (!customElements.get('ryvt-gallery')) {
    customElements.define(
      'ryvt-gallery',
      class extends HTMLElement {
        connectedCallback() {
          this.track = q(A('track'), this);
          this._onVariant = (detail) => this.applyColor(detail ? detail.color : '');
          R.on('ryvt:variant:change', this._onVariant);

          this._ac = new AbortController();
          const s = { signal: this._ac.signal };
          this.addEventListener(
            'click',
            (e) => {
              const jump = e.target.closest(A('dot'));
              if (jump) {
                this.goTo(jump.dataset.ryvtMediaId);
                return;
              }
              const zoomBtn = e.target.closest(A('zoom-open'));
              if (zoomBtn) openZoom(zoomBtn.closest(A('gallery-item')), zoomBtn);
            },
            s
          );
          if (this.track) {
            this.track.addEventListener(
              'scroll',
              () => {
                cancelAnimationFrame(this._raf);
                this._raf = requestAnimationFrame(() => this.syncActive());
              },
              { passive: true, signal: this._ac.signal }
            );
          }

          /* Seed from the chip the server already rendered. ryvt:variant:change
             fires on SIZE changes too and always carries the current colour, so
             without a starting value the first size a shopper picks would read
             as a colour transition and yank the page up to the gallery — worst
             from the sticky bar, whose size control is nowhere near it. */
          const section = this.closest(SECTION);
          const chip = section && q('[data-option-name="Color"] input' + OPT_INPUT + ':checked', section);
          this._match = this.track ? this.matchedColor(chip && chip.value) : '';

          this.syncLead();
          this.syncActive();
        }

        disconnectedCallback() {
          R.off('ryvt:variant:change', this._onVariant);
          if (this._ac) this._ac.abort();
          cancelAnimationFrame(this._raf);
        }

        /* '' when the selected colour has no media of its own — the gallery then
           shows ALL media rather than going empty. The visible stack is a pure
           function of this value, which makes it the identity applyColor uses to
           decide whether anything changed: MATCHED, not selected, because two
           colourways that both fall back to "show everything" render the same
           stack and must not fade or move the page. */
        matchedColor(color) {
          const wanted = norm(color);
          if (!wanted || !this.track) return '';
          const hit = qa(A('gallery-item'), this.track).some(
            (li) => !!li.dataset.ryvtColor && norm(li.dataset.ryvtColor) === wanted
          );
          return hit ? wanted : '';
        }

        applyColor(color) {
          if (!this.track) return;
          /* Flat lay first, then model views, other colored media, and lifestyle. */
          const rank = (el) => {
            const ds = el.dataset;
            return !ds.ryvtColor ? 3 : ds.ryvtKind === 'ghost' ? 0 : ds.ryvtKind === 'model' ? 1 : 2;
          };
          const lists = [qa(A('gallery-item'), this.track), qa(A('dot'), this)];
          const slides = lists[0];
          if (!slides.length) return;

          const matchColor = this.matchedColor(color);
          /* Bail on anything that is not a colour transition — a size change, or
             a re-click of the chip already active. The filter and the sort below
             are a pure function of matchColor, so re-running them would rebuild
             the identical DOM: pointless node churn, a mask fade over nothing,
             and a page scroll the shopper did not ask for. */
          if (matchColor === this._match) return;
          this._match = matchColor;

          lists.forEach((items) => {
            if (!items.length) return;
            const parent = items[0].parentElement;
            items.forEach((el) => {
              const c = el.dataset.ryvtColor;
              el.hidden = !(!c || !matchColor || norm(c) === matchColor);
            });
            if (!parent) return;
            /* The view is part of the sort key, not a tiebreak the media index
               gets to overwrite: Liquid orders (rank, view, index) — see
               sortRank in exports/src/lib/media-audit.ts — so a rank-then-index
               sort here would re-order the server's markup on the first swatch
               click and drop each colourway back onto whichever shot happens to
               sit earliest in product.media (Black led on a side view). */
            items
              .map((el) => ({
                el,
                i: Number(el.dataset.ryvtIndex) || 0,
                r: rank(el),
                v: Number(el.dataset.ryvtView) || 0
              }))
              .sort((a, b) => a.r - b.r || a.v - b.v || a.i - b.i)
              .forEach((entry) => parent.appendChild(entry.el));
          });

          this.syncLead();

          /* Mask the whole visible stack, not the lead alone: above 990px every
             item is on screen at once, so fading only the first would show the
             rest of the grid swap with no transition at all.
             Skipped outright under reduced motion. --ryvt-t-mask being 0ms
             there only zeroes the fade BACK: .is-masking sets transition: none
             on the way in, so the class would still land as a one-frame blank
             flash of the whole stack. */
          if (!reduced.matches) {
            const visible = qa(A('gallery-item') + ':not([hidden])', this.track);
            visible.forEach((el) => el.classList.add('is-masking'));
            /* Commit the opacity: 0 before the rAF takes the class off again.
               A rAF callback runs BEFORE the style pass of the cycle it belongs
               to, so without an explicit flush the add and the remove collapse
               into a single recalc and no transition ever runs. This must not
               be left to an incidental layout read elsewhere in the method. */
            this.track.offsetHeight;
            requestAnimationFrame(() => visible.forEach((el) => el.classList.remove('is-masking')));
          }
          if (carousel.matches) {
            this.track.scrollLeft = 0;
          } else if (this.getBoundingClientRect().top < 0) {
            /* Only pull the page up when the gallery has already scrolled past
               the top of the viewport — otherwise changing colour would yank a
               shopper who is reading the buy rail back up the page. */
            this.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
          this.syncActive();
        }

        syncLead() {
          if (!this.track) return;
          const slides = qa(A('gallery-item'), this.track);
          const lead = slides.find((slide) => !slide.hidden);
          slides.forEach((slide) => {
            const isLead = slide === lead;
            slide.toggleAttribute('data-ryvt-gallery-lead', isLead);
            const image = q('.ryvt-gallery__media img', slide);
            if (image) image.sizes = isLead
              ? '(min-width: 990px) 60vw, calc(100vw - 40px)' // keep equal to main-product.liquid's lead `sizes` and its preload
              : '(min-width: 990px) 30vw, 100vw';
          });
        }

        syncActive() {
          if (!this.track) return;
          const slides = qa(A('gallery-item') + ':not([hidden])', this.track);
          if (!slides.length) return;
          /* Only the carousel has a scroll offset to read. On the desktop grid
             the track never scrolls horizontally, so scrollLeft / clientWidth
             would pin idx at 0 anyway — and the lead item IS the active one
             there, the whole stack being visible at once. */
          let idx = 0;
          if (carousel.matches) {
            const width = this.track.clientWidth || 1;
            idx = Math.min(slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
          }
          const activeId = slides[idx].dataset.ryvtMediaId;
          qa(A('dot'), this).forEach((el) => {
            const isActive = el.dataset.ryvtMediaId === activeId;
            tog(el, 'is-active', isActive);
            el.setAttribute('aria-current', isActive ? 'true' : 'false');
          });
        }

        goTo(mediaId) {
          if (!this.track || !mediaId) return;
          const slide = q(A('gallery-item') + A('media-id="' + mediaId + '"'), this.track);
          if (!slide || slide.hidden) return;
          if (carousel.matches) {
            this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior() });
            return;
          }
          /* Desktop grid: the track has no horizontal overflow to scroll, so
             bringing an item into view means scrolling the PAGE to it. */
          slide.scrollIntoView({ block: 'start', behavior: behavior() });
        }
      }
    );
  }

  /* Keep the sticky purchase rail accessible when native wallets or app blocks
     change its height. Its grid-row wrapper, not the specs row, is the boundary. */
  if (!customElements.get('ryvt-purchase-column')) {
    customElements.define(
      'ryvt-purchase-column',
      class extends HTMLElement {
        connectedCallback() {
          const rail = q('.ryvt-pdp__buy', this);
          if (!rail) return;
          const measure = () => this.style.setProperty('--ryvt-buy-height', rail.offsetHeight + 'px');
          measure();
          this._ro = new ResizeObserver(measure);
          this._ro.observe(rail);
        }

        disconnectedCallback() {
          if (this._ro) this._ro.disconnect();
        }
      }
    );
  }

  /* -- <ryvt-sticky-bar> (ported IO + direction test, R10 triggers) ---------- */
  if (!customElements.get('ryvt-sticky-bar')) {
    customElements.define(
      'ryvt-sticky-bar',
      class extends HTMLElement {
        connectedCallback() {
          this.section = this.closest(SECTION);
          this._atcBlock = q(A('atc-block'), this.section);
          this._mobile = matchMedia('(max-width: 749px)');
          this._ticking = false;

          /* Redesign §7.4: visibility is MEASURED, not observed. The previous
             IntersectionObserver never fired on a single-frame teleport past
             the buy block — an anchor jump, a restored scroll position on
             back-navigation, find-in-page — because the intersection ratio
             went 0 -> 0 with no transition, so the bar stayed hidden at the
             exact depth it exists for (reproduced live 2026-08-22). One rect
             read per scroll frame, rAF-throttled, replaces it; the measured
             truth also covers the initial paint at a restored deep position. */
          this._measure = () => {
            this._ticking = false;
            if (!this._atcBlock) return;
            const modalOpen = document.documentElement.classList.contains('ryvt-scroll-locked') || !!q('.ryvt-zoom.is-open');
            // Mobile: the bar stands aside only while the inline primary button
            // that is currently shown (Add to bag, or Notify when sold out) sits
            // whole between the pinned header and the viewport bottom, so the
            // two never double up and a purchase action is always in reach.
            // Desktop retains its existing handoff after the main purchase block.
            let show;
            if (this._mobile.matches) {
              const btn = [q(A('atc'), this.section), q(A('atc-notify'), this.section)].find((el) => el && !el.hidden);
              // The sticky wrapper is display:contents (zero rect); its children are the boxes.
              const header = q('.ryvt-header-sticky');
              const top = header ? Math.max(0, ...[...header.children].map((el) => el.getBoundingClientRect().bottom)) : 0;
              const r = btn && btn.getBoundingClientRect();
              show = !(r && r.height && r.top >= top && r.bottom <= window.innerHeight);
            } else {
              show = this._atcBlock.getBoundingClientRect().bottom < 0;
            }
            tog(this, 'is-visible', show && !modalOpen);
          };
          this._queue = () => {
            if (this._ticking) return;
            this._ticking = true;
            requestAnimationFrame(this._measure);
          };

          this._ac = new AbortController();
          const s = { signal: this._ac.signal };
          window.addEventListener('scroll', this._queue, { passive: true, signal: this._ac.signal });
          window.addEventListener('resize', this._queue, { passive: true, signal: this._ac.signal });

          this._onVariant = (detail) => {
            this.syncContent(detail || {});
            this._queue();
          };
          this._onSurface = () => this._queue();
          R.on('ryvt:variant:change', this._onVariant);
          R.on('ryvt:drawer:open', this._onSurface);
          R.on('ryvt:drawer:close', this._onSurface);

          this._queue();

          this.addEventListener(
            'click',
            (e) => {
              const btn = e.target.closest(A('sticky-submit'));
              if (btn) this.submit(btn);
            },
            s
          );
          this.addEventListener(
            'change',
            (e) => {
              const sel = e.target.closest(A('sticky-size'));
              if (!sel || !sel.value || !this.section) return;
              const radio = q(SIZE_INPUT + '[value="' + CSS.escape(sel.value) + '"]', this.section);
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            },
            s
          );
        }

        disconnectedCallback() {
          if (this._ac) this._ac.abort();
          R.off('ryvt:variant:change', this._onVariant);
          R.off('ryvt:drawer:open', this._onSurface);
          R.off('ryvt:drawer:close', this._onSurface);
        }

        syncContent(detail) {
          const variant = detail.variant || null;
          const sec = this.section;
          if (!sec) return;

          const picker = q('ryvt-variant-picker', sec);
          const variantEl = q(A('sticky-variant'), this);
          const showVariantTitle = !!(picker && picker.data && picker.data.showVariantTitle);
          if (variantEl) variantEl.textContent = showVariantTitle && variant ? variant.title : '';

          /* price text copied verbatim from the server-rendered bank */
          const bankEntry = variant ? q(A('variant-price="' + variant.id + '"'), sec) : q(A('unresolved-price'), sec);
          const priceEl = q(A('sticky-price'), this);
          const text = bankEntry && bankEntry.dataset.priceText;
          if (priceEl && text) priceEl.textContent = text;

          tog(this, 'is-notify', !!(variant && !variant.available));
          tog(this, 'is-select', !variant);

          const sel = q(A('sticky-size'), this);
          if (sel) {
            const checked = q(SIZE_INPUT + ':checked', sec);
            sel.value = checked ? checked.value : '';
          }
        }

        submit(invoker) {
          const sec = this.section;
          if (!sec) return;
          const picker = q('ryvt-variant-picker', sec);
          if (this.classList.contains('is-notify')) {
            openSheet('notify', invoker);
            return;
          }
          if (this.classList.contains('is-select')) {
            if (picker && picker.pulseSizes) picker.pulseSizes(() => this.submit(invoker));
            return;
          }
          const form = q(A('product-form'), sec);
          if (form && form.requestSubmit) form.requestSubmit();
        }
      }
    );
  }

  /* -- zoom overlay ----------------------------------------------------------- */
  let releaseTrap = null;
  const overlayInvokers = new WeakMap();
  const zoomState = { items: [], index: 0, touchX: null };

  function openOverlay(root, focusEl, invoker) {
    if (!root) return;
    overlayInvokers.set(root, invoker || document.activeElement);
    root.hidden = false;
    root.offsetHeight;
    root.classList.add('is-open');
    document.documentElement.classList.add('ryvt-pdp-overlay-open');
    releaseTrap = R.trapFocus(root);
    if (focusEl) focusEl.focus();
  }

  function closeOverlay(root) {
    if (!root || root.hidden) return;
    const invoker = overlayInvokers.get(root);
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ryvt-pdp-overlay-open');
    if (releaseTrap) {
      releaseTrap();
      releaseTrap = null;
    }
    setTimeout(() => {
      root.hidden = true;
      if (invoker && invoker.focus) invoker.focus({ preventScroll: true });
      overlayInvokers.delete(root);
    }, 200);
  }

  function renderZoom(index) {
    const zoom = q(A('zoom'));
    if (!zoom) return;
    const count = zoomState.items.length;
    if (!count) return;
    zoomState.index = (index + count) % count;
    const item = zoomState.items[zoomState.index];
    const img = q(A('zoom-img'), zoom);
    if (img) {
      img.src = item.dataset.zoomUrl || img.src;
      img.alt = item.dataset.zoomAlt || '';
    }
    const counter = q(A('zoom-counter'), zoom);
    if (counter) counter.textContent = String(zoomState.index + 1) + ' / ' + String(count);
    qa(A('zoom-prev') + ', ' + A('zoom-next'), zoom).forEach((button) => {
      button.hidden = count < 2;
    });
  }

  function stepZoom(delta) {
    if (!zoomState.items.length) return;
    renderZoom(zoomState.index + delta);
  }

  function openZoom(item, invoker) {
    if (!item) return;
    const zoom = q(A('zoom'));
    if (!zoom) return;
    const gallery = item.closest(A('gallery'));
    zoomState.items = qa(A('gallery-item'), gallery).filter((candidate) => !candidate.hidden);
    zoomState.index = Math.max(0, zoomState.items.indexOf(item));
    renderZoom(zoomState.index);
    openOverlay(zoom, q(A('zoom-close'), zoom), invoker);
  }

  /* sheet openers ('size-guide' | 'notify'); tab pre-select for the size guide */
  function openSheet(kind, invoker, tabTarget) {
    const sheet = q(kind === 'size-guide' ? A('size-guide-sheet') : A('notify-sheet'));
    if (!sheet || !sheet.open) return;
    if (kind === 'size-guide') {
      const tab = tabTarget === 'find' ? 'find' : 'chart';
      activateTab(sheet, tab);
      const picker = q('ryvt-variant-picker');
      const product = picker && picker.data && picker.data.product;
      R.track('size_guide_view', {
        product_id: product ? Number(product.id) : undefined,
        product_handle: product && product.handle,
        tab,
      });
    }
    sheet.open(invoker);
  }

  /* -- size-guide tabs + lazy recommender ------------------------------------- */
  function activateTab(sheet, name) {
    if (!sheet) return;
    qa(A('tab'), sheet).forEach((tab) => {
      const active = tab.dataset.ryvtTab === name;
      tog(tab, 'is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    qa(A('tab-panel'), sheet).forEach((panel) => {
      const active = panel.dataset.ryvtTabPanel === name;
      panel.hidden = !active;
      if (active && name === 'find') {
        const url = panel.dataset.ryvtRecommenderModule;
        if (url && !panel.hasAttribute('data-module-loaded')) {
          panel.setAttribute('data-module-loaded', '');
          import(url).catch(() => panel.removeAttribute('data-module-loaded'));
        }
      }
    });
  }

  /* -- document-level PDP delegation ------------------------------------------ */
  D.addEventListener('click', (e) => {
    const t = e.target;

    const recommenderApply = t.closest(A('size-apply'));
    if (recommenderApply) {
      const recommender = recommenderApply.closest('ryvt-size-recommender');
      const picker = q('ryvt-variant-picker');
      const product = picker && picker.data && picker.data.product;
      R.track('size_recommender_apply', {
        product_id: product ? Number(product.id) : undefined,
        product_handle: product && product.handle,
        recommended_size: recommender && recommender.recommended,
      });
    }

    const opener = t.closest(A('open-sheet'));
    if (opener) {
      e.preventDefault();
      openSheet(opener.dataset.ryvtOpenSheet, opener, opener.dataset.ryvtTabTarget);
      return;
    }

    const tab = t.closest(A('tab'));
    if (tab) {
      activateTab(tab.closest('.ryvt-drawer'), tab.dataset.ryvtTab);
      return;
    }

    const zoomPrevious = t.closest(A('zoom-prev'));
    if (zoomPrevious) {
      e.preventDefault();
      stepZoom(-1);
      return;
    }

    const zoomNext = t.closest(A('zoom-next'));
    if (zoomNext) {
      e.preventDefault();
      stepZoom(1);
      return;
    }

    const closer = t.closest(A('zoom-close'));
    if (closer) closeOverlay(closer.closest(A('zoom')));
  });

  D.addEventListener(
    'touchstart',
    (e) => {
      if (!e.target.closest || !e.target.closest(A('zoom-stage'))) return;
      zoomState.touchX = e.changedTouches[0] ? e.changedTouches[0].clientX : null;
    },
    { passive: true }
  );

  D.addEventListener(
    'touchend',
    (e) => {
      if (zoomState.touchX === null || !e.target.closest || !e.target.closest(A('zoom-stage'))) return;
      const endX = e.changedTouches[0] ? e.changedTouches[0].clientX : zoomState.touchX;
      const delta = endX - zoomState.touchX;
      zoomState.touchX = null;
      if (Math.abs(delta) < 48) return;
      stepZoom(delta > 0 ? -1 : 1);
    },
    { passive: true }
  );

  D.addEventListener('keydown', (e) => {
    const openZoomRoot = q(A('zoom') + ':not([hidden])');
    if (openZoomRoot && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      stepZoom(e.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    const tab = e.target.closest && e.target.closest(A('tab'));
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      const tabs = qa(A('tab'), tab.closest('[role="tablist"]'));
      if (!tabs.length) return;
      e.preventDefault();
      const index = tabs.indexOf(tab);
      let nextIndex = index;
      if (e.key === 'Home') nextIndex = 0;
      else if (e.key === 'End') nextIndex = tabs.length - 1;
      else if (e.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      else nextIndex = (index - 1 + tabs.length) % tabs.length;
      const next = tabs[nextIndex];
      activateTab(next.closest('.ryvt-drawer'), next.dataset.ryvtTab);
      next.focus();
      return;
    }
    if (e.key !== 'Escape') return;
    closeOverlay(q(A('zoom') + ':not([hidden])'));
  });

  /* notify-sheet success round trip: reopen so the confirmation is visible */
  const posted = q(A('open-on-load'));
  if (posted) {
    const sheet = posted.closest(A('notify-sheet'));
    if (sheet && sheet.open) {
      sheet.open();
      posted.focus({ preventScroll: true });
    }
  }

  R.on('ryvt:quiz:recommended', (detail) => {
    const picker = q('ryvt-variant-picker');
    const product = picker && picker.data && picker.data.product;
    R.track('size_recommender_complete', {
      product_id: product ? Number(product.id) : undefined,
      product_handle: product && product.handle,
      recommended_size: detail && detail.size,
    });
  });
  }

  if (window.RYVT) init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });
})();
